/**
 * Dealer Principal dashboard.
 *
 * Dealer-lens questions this page must answer without a drill-down:
 *   - What do I need to know?            → row 1 (today) + row 2 (inflow / activity)
 *   - What decisions do I need to make?  → row 3 (exceptions)
 *   - What should come to my attention?  → row 3, and the tinted row-1 tile
 *
 * No dollar value appears above row 5. "The dollar value is probably more of a
 * distraction rather than it being a motivator or useful. I don't think anyone
 * will actually look at that." Where money is decision-relevant it is a plain
 * secondary total, never a weighted derivation.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import KpiTile from '../../components/dashboard/KpiTile';
import { splitSub } from '../../components/dashboard/NewUsedSplit';
import { DonutChart, ColumnChart, RingGauge, PipelineFunnel, StackedHBars } from '../../components/dashboard/Pictorial';
import { ExceptionSummary } from '../../components/dashboard/ExceptionList';
import { AreaSpark } from '../../components/Charts';
import { buildDpBuckets } from '../../lib/funnelStages';

const splitSegments = (s = {}) => [
  { label: 'New',          value: s.new,     color: 'var(--info)' },
  { label: 'Used',         value: s.used,    color: 'var(--success)' },
  { label: 'Demo',         value: s.demo,    color: 'var(--warning)' },
  { label: 'Unclassified', value: s.unknown, color: 'var(--text-muted)' },
];

const BUCKET_COLORS = {
  INBOUND:   'color-mix(in srgb, var(--accent-primary) 45%, var(--bg-secondary))',
  WORKING:   'color-mix(in srgb, var(--accent-primary) 65%, var(--bg-secondary))',
  IN_DEAL:   'var(--accent-primary)',
  CONFIRMED: 'color-mix(in srgb, var(--success) 80%, var(--bg-secondary))',
  DELIVERED: 'var(--success)',
};

export default function DealerPrincipalDashboard({ snapshot, periodLabel, series }) {
  const s = snapshot;
  const t = s.today;
  const w = s.window;
  const ex = s.exceptions;
  const exceptionTotal = ex
    ? (ex.unactionedLeads + ex.unassignedLeads + ex.leadsWithoutNextEvent + ex.aiNurtureExpiringSoon)
    : null;
  const [funnelCat, setFunnelCat] = useState('ALL');

  return (
    <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {s.legacy && (
        <div className="mono text-xs" style={{ color: 'var(--warning)' }}>
          Showing the legacy snapshot — today's counts, new/used split and exceptions arrive with the role-snapshot endpoint.
        </div>
      )}

      {/* ── ROW 1: today, count-based ── */}
      <div className="grid-5">
        <KpiTile index={0} label="APPOINTMENTS" periodLabel="Today" to="/appointments"
          value={t?.appointments ?? '—'}
          sub={t ? `${t.appointmentsConfirmed ?? 0} confirmed · ${t.appointmentsRequested ?? 0} requested` : 'needs role-snapshot'} />
        <KpiTile index={1} label="NEW LEADS" periodLabel="Today" to="/leads"
          value={t?.newLeads ?? w.newLeads.total}
          sub={t ? splitSub({ new: t.newLeadsNew, used: t.newLeadsUsed, unknown: t.newLeadsUnknown }) : `${periodLabel} window`} />
        <KpiTile index={2} label="TEST DRIVES" periodLabel="Today" to="/test-drives"
          value={t?.testDrives ?? '—'} />
        <KpiTile index={3} label="DELIVERIES" periodLabel="Today" to="/appointments"
          value={t?.deliveries ?? '—'} color="var(--success)" />
        <KpiTile index={4} label="EXCEPTIONS" periodLabel="Now" to="/leads?filter=exceptions"
          value={exceptionTotal ?? '—'}
          color={exceptionTotal > 0 ? 'var(--danger)' : 'var(--success)'}
          tone={exceptionTotal > 0 ? 'danger' : 'success'}
          sub={exceptionTotal > 0 ? 'need a decision' : 'all clear'}
          tooltip="Unactioned, unassigned, no next step, AI nurture expiring" />
      </div>

      {/* ── ROW 2: inflow + activity over the period ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="card">
          <div className="flex-between" style={{ marginBottom: 8 }}>
            <span className="card-label" style={{ margin: 0 }}>LEAD INFLOW · {periodLabel.toUpperCase()}</span>
            <Link to="/leads" className="mono text-xs" style={{ color: 'var(--accent-primary-hover)', textDecoration: 'none' }}>open board</Link>
          </div>
          <DonutChart segments={splitSegments(w.newLeads)} centerLabel="leads" hideZero />
          {series?.leads?.length > 1 && (
            <div style={{ marginTop: 10 }}>
              <div className="mono text-xs text-muted" style={{ marginBottom: 4 }}>Daily trend</div>
              <AreaSpark values={series.leads} w={320} h={40} color="var(--accent-primary)" />
            </div>
          )}
        </div>
        <div className="card">
          <div className="card-label" style={{ marginBottom: 8 }}>SALES ACTIVITY · {periodLabel.toUpperCase()}</div>
          <ColumnChart bars={[
            { label: 'Booked',     value: w.appointments.booked,   color: 'color-mix(in srgb, var(--accent-primary) 55%, var(--bg-secondary))' },
            { label: 'Attended',   value: w.appointments.attended, color: 'var(--accent-primary)' },
            { label: 'No-show',    value: w.appointments.noShow,   color: 'var(--danger)' },
            { label: 'Test drives', value: w.testDrives.total,     color: 'var(--info)' },
            { label: 'Sold',       value: w.sold.total,            color: 'color-mix(in srgb, var(--success) 75%, var(--bg-secondary))' },
            { label: 'Delivered',  value: w.deliveries,            color: 'var(--success)' },
          ]} />
          <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
            Test drives: {splitSub(w.testDrives)} · Sold: {splitSub(w.sold)}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex-between" style={{ marginBottom: 8 }}>
          <span className="card-label" style={{ margin: 0 }}>SALES FUNNEL · {periodLabel.toUpperCase()}</span>
          <div className="mode-toggle" role="tablist">
            {['ALL', 'NEW', 'USED'].map((c) => (
              <button key={c} role="tab" aria-selected={funnelCat === c} className={funnelCat === c ? 'active' : ''} onClick={() => setFunnelCat(c)}>{c[0] + c.slice(1).toLowerCase()}</button>
            ))}
          </div>
        </div>
        <DpFunnel funnel={w.funnel} cat={funnelCat} />
      </div>

      {/* ── ROW 3: the decision list ── */}
      <div className="card">
        <div className="flex-between" style={{ marginBottom: 8 }}>
          <span className="card-label" style={{ margin: 0 }}>EXCEPTIONS & ESCALATIONS</span>
          <Link to="/leads?filter=exceptions" className="mono text-xs" style={{ color: 'var(--accent-primary-hover)', textDecoration: 'none' }}>view all</Link>
        </div>
        <ExceptionSummary exceptions={ex} />
      </div>

      {/* ── ROW 4: locations, only when there are several ── */}
      {Array.isArray(s.locations) && s.locations.length > 1 && (
        <div className="card" style={{ padding: 0 }}>
          <div className="card-label" style={{ padding: '12px 14px 0' }}>LOCATION PERFORMANCE · {periodLabel.toUpperCase()}</div>
          <div style={{ padding: '10px 14px' }}>
            <StackedHBars
              series={[
                { key: 'appointments', label: 'Appointments', color: 'var(--accent-primary)' },
                { key: 'testDrives',   label: 'Test drives',  color: 'var(--info)' },
                { key: 'sold',         label: 'Sold',         color: 'var(--success)' },
              ]}
              rows={s.locations.map((l) => ({ key: l.id, label: l.name, parts: { appointments: l.appointments, testDrives: l.testDrives, sold: l.sold } }))}
            />
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr><th>Location</th><th>New leads</th><th>Appointments</th><th>Test drives</th><th>Sold</th></tr>
              </thead>
              <tbody>
                {s.locations.map((l) => (
                  <tr key={l.id}>
                    <td>{l.name}</td>
                    <td className="mono">{l.newLeads ?? 0}</td>
                    <td className="mono">{l.appointments ?? 0}</td>
                    <td className="mono">{l.testDrives ?? 0}</td>
                    <td className="mono">{l.sold ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── ROW 5: secondary ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        <div className="card">
          <div className="card-label" style={{ marginBottom: 8 }}>CONVERSION · {periodLabel.toUpperCase()}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            <RingGauge value={w.conversion?.leadToAppointment} label="Lead → qualified" size={80} />
            <RingGauge value={w.conversion?.appointmentToTestDrive} label="Qualified → test drive" color="var(--info)" size={80} />
            <RingGauge value={w.conversion?.testDriveToSale} label="Test drive → sale" color="var(--success)" size={80} />
          </div>
        </div>
        <div className="card">
          <div className="card-label" style={{ marginBottom: 8 }}>AI vs HUMAN</div>
          {s.aiVsHuman ? (
            <DonutChart size={96} centerLabel="leads" segments={[
              { label: 'AI',     value: s.aiVsHuman.aiOwned,    color: 'var(--info)' },
              { label: 'Human',  value: s.aiVsHuman.humanOwned, color: 'var(--accent-primary)' },
              { label: 'Shared', value: s.aiVsHuman.shared,     color: 'var(--warning)' },
            ]} />
          ) : <div className="mono text-xs text-muted">Needs role-snapshot.</div>}
        </div>
        <div className="card">
          <div className="card-label" style={{ marginBottom: 8 }}>ADVANCE PAYMENTS · TODAY</div>
          {t?.advancePaymentsCount != null ? (
            <>
              <Line label="Received" value={t.advancePaymentsCount} />
              <Line label="Value" value={`$${Number(t.advancePaymentsValue || 0).toLocaleString('en-AU')}`} last />
            </>
          ) : <div className="mono text-xs text-muted">Available once payments go live.</div>}
        </div>
      </div>

      {/* ── ROW 6: shortcuts (sales-relevant only) ── */}
      <div className="grid-4">
        {[
          ['LEAD ACCELERATOR', '/leads', `${w.totalLeads} active · ${w.hotLeads} hot`],
          ['APPOINTMENTS', '/appointments', 'Diary, deliveries, test drives'],
          ['CUSTOMERS', '/customers', `${w.totalCustomers} in CRM`],
          ['LOST LEADS', '/dead-leads', 'Revival campaigns'],
        ].map(([title, to, sub]) => (
          <Link key={to} to={to} className="card module-tile" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className="flex-between"><span className="card-label" style={{ margin: 0 }}>{title}</span><span className="module-arrow">→</span></div>
            <div className="mono text-xs text-muted" style={{ marginTop: 6 }}>{sub}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}


function DpFunnel({ funnel, cat }) {
  const buckets = buildDpBuckets(funnel, cat);
  if (!buckets) {
    return <div className="mono text-xs text-muted">Needs the role-snapshot endpoint.</div>;
  }
  if (buckets.isEmpty) {
    return (
      <div className="mono text-xs text-muted" style={{ padding: '12px 0' }}>
        No leads moved through the pipeline in this window. Older leads are still open —
        widen the period to see them.
      </div>
    );
  }
  return (
    <PipelineFunnel stages={buckets.map((b) => ({ ...b, color: BUCKET_COLORS[b.key] }))} />
  );
}

function Line({ label, value, sub, last }) {
  return (
    <div className="flex-between" style={{ padding: '6px 0', borderBottom: last ? 'none' : '1px solid var(--border-subtle)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ textAlign: 'right' }}>
        <span className="mono" style={{ fontSize: 14, fontWeight: 600 }}>{value ?? '—'}</span>
        {sub && <div className="mono text-xs text-muted">{sub}</div>}
      </span>
    </div>
  );
}
